import React, { useMemo, useState } from 'react';
import { ACAO_LABEL, ORIGEM_LABEL, nomeFase, diffConfigs, curto } from './promocoesWhitelabelUtil';

/**
 * Peças de revisão de uma promoção de whitelabel, usadas pela revisão por proposta
 * (PromocaoRevisao) e pelo resumo embutido da página de lote (PromocaoLote).
 */

export const valeNaHora = (it) => it.acao === 'editar' && !!it.target_active;
export const entraGuardado = (it) => it.acao === 'criar' || (it.acao === 'editar' && !it.target_active);
export const nomeItem = (it) => (it.nivel === 'fase' ? `${nomeFase(it)} — ${it.name}` : it.name);
export const aplicavel = (it) => it.acao === 'criar' || it.acao === 'editar';

export function diffDoItem(it) {
    const base = it.acao === 'criar' ? {} : (it.atual && it.atual.config) || {};
    return diffConfigs(base, it.config);
}

export function DiffTable({ diff }) {
    if (diff.itens.length === 0) return <div className="vr-empty">Sem diferenças de conteúdo.</div>;
    return (
        <>
            <table className="vr-table wl-diff">
                <thead><tr><th>Caminho</th><th>Mudança</th><th>Aqui hoje</th><th>Proposto</th></tr></thead>
                <tbody>
                    {diff.itens.map((d, i) => (
                        <tr key={i} className={`wl-d-${d.tipo}`}>
                            <td className="wl-path">{d.path || '(raiz)'}</td>
                            <td><span className="vr-chip">{d.tipo}</span></td>
                            <td className="wl-val">{curto(d.antes)}</td>
                            <td className="wl-val">{curto(d.depois)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {diff.truncado && <div className="vr-muted vr-small" style={{ padding: 8 }}>Mostrando as primeiras {diff.itens.length} diferenças.</div>}
        </>
    );
}

const contagem = (diff) => {
    const c = {};
    diff.itens.forEach((d) => { c[d.tipo] = (c[d.tipo] || 0) + 1; });
    return Object.entries(c).map(([t, n]) => `${n} ${t}`).join(' · ');
};

/** Um item aplicável, recolhível, com o painel de diferenças em foco e as demais visões em abas. */
function ItemInline({ it, origem, aberto, onToggle }) {
    const diff = useMemo(() => diffDoItem(it), [it]);
    const [aba, setAba] = useState('diferencas');
    const abas = [['diferencas', 'Diferenças'], ['proposto', 'Proposto'], ['atual', 'Aqui hoje'], ['dados', 'Dados']];
    return (
        <div className={`wl-inl-item ${valeNaHora(it) ? 'live' : ''}`}>
            <button className="wl-inl-head" aria-expanded={aberto} onClick={onToggle}>
                <span className="wl-caret">{aberto ? '▾' : '▸'}</span>
                <span className="vr-chip">{it.nivel === 'fase' ? 'Fase' : 'Empresa'}</span>
                <span className="wl-inl-name">{nomeItem(it)}</span>
                <span className="vr-chip vr-chip-partial">{ACAO_LABEL[it.acao] || it.acao}</span>
                {valeNaHora(it) && <span className="vr-chip wl-chip-live">ATIVO · vale na hora</span>}
                {entraGuardado(it) && <span className="vr-chip">entra guardado</span>}
                <span className="vr-muted vr-small wl-inl-count">{contagem(diff) || 'sem diferenças de conteúdo'}</span>
            </button>
            {aberto && (
                <div className="wl-inl-body">
                    <div className="vr-muted vr-small wl-item-facts">
                        <span>{it.acao === 'criar' ? 'Config novo: tudo é adicionado' : `Contra o config #${it.target_config_id} daqui`}</span>
                        {it.alcance && <span title={it.alcance.base}>Alcança: {it.alcance.vendedores} vendedor{it.alcance.vendedores === 1 ? '' : 'es'}</span>}
                    </div>
                    {it.mudou_desde_a_proposta && (
                        <div className="wl-motivo">Este config foi alterado aqui depois da proposta: a aprovação será recusada. Peça uma proposta nova.</div>
                    )}
                    {valeNaHora(it) && <div className="vr-mbox warn">Config <strong>ATIVO</strong>: as diferenças passam a valer para os vendedores assim que a proposta for aprovada.</div>}
                    <div className="wl-inl-tabs" role="tablist">
                        {abas.map(([k, l]) => (
                            <button key={k} role="tab" aria-selected={aba === k} className={`vr-side-tab ${aba === k ? 'active' : ''}`} onClick={() => setAba(k)}>{l}</button>
                        ))}
                    </div>
                    <div className="wl-inl-scroll">
                        {aba === 'diferencas' && <DiffTable diff={diff} />}
                        {aba === 'proposto' && <pre className="vr-pre">{JSON.stringify(it.config, null, 2)}</pre>}
                        {aba === 'atual' && (it.atual && it.atual.config
                            ? <pre className="vr-pre">{JSON.stringify(it.atual.config, null, 2)}</pre>
                            : <div className="vr-muted">Não existe aqui: será criado, guardado.</div>)}
                        {aba === 'dados' && (
                            <dl className="wl-dados">
                                <dt>md5 proposto</dt><dd>{it.config_md5}</dd>
                                <dt>md5 aqui na proposta</dt><dd>{it.target_md5 || '—'}</dd>
                                <dt>md5 aqui agora</dt><dd>{(it.atual && it.atual.md5) || '—'}</dd>
                                <dt>ativo aqui</dt><dd>{it.atual ? (it.atual.active ? 'sim' : 'não') : '—'}</dd>
                                <dt>origem</dt><dd>{ORIGEM_LABEL[origem] || origem} · config #{it.origem_config_id ?? '—'}</dd>
                                {it.nivel === 'fase' && <><dt>fase aqui</dt><dd>{nomeFase(it)} (#{it.phase_id})</dd></>}
                            </dl>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/** Resumo de uma proposta dentro da página de lote: grupos de impacto + itens com o diff em foco. */
export function ResumoInline({ p }) {
    const itens = p.itens;
    const aplicaveis = itens.filter(aplicavel);
    const [fechados, setFechados] = useState({});      // por padrão todo item aplicável abre
    const alterna = (id) => setFechados((f) => ({ ...f, [id]: !f[id] }));
    const todos = (abrir) => setFechados(abrir ? {} : Object.fromEntries(aplicaveis.map((it) => [it.id, true])));
    const vivos = aplicaveis.filter(valeNaHora).length;
    const guardados = aplicaveis.filter(entraGuardado).length;
    const iguais = itens.filter((it) => it.acao === 'sem_mudanca');
    const recusados = itens.filter((it) => it.acao === 'recusado');
    return (
        <div className="wl-inl">
            <div className="wl-inl-summary">
                <span className={`vr-chip ${vivos ? 'wl-chip-live' : ''}`}>Vale na hora: {vivos}</span>
                <span className="vr-chip">Entra guardado: {guardados}</span>
                <span className="vr-chip">Não muda: {iguais.length}</span>
                <span className={`vr-chip ${recusados.length ? 'vr-chip-denied' : ''}`}>Recusados: {recusados.length}</span>
                {aplicaveis.length > 1 && (
                    <span className="wl-inl-tools">
                        <button className="vr-btn vr-btn-sm" onClick={() => todos(true)}>Expandir itens</button>
                        <button className="vr-btn vr-btn-sm" onClick={() => todos(false)}>Recolher itens</button>
                    </span>
                )}
            </div>
            {aplicaveis.length === 0 && <div className="vr-muted">Nenhum item desta proposta muda o conteúdo.</div>}
            {aplicaveis.map((it) => (
                <ItemInline key={it.id} it={it} origem={p.cabecalho.origem} aberto={!fechados[it.id]} onToggle={() => alterna(it.id)} />
            ))}
            {[...iguais, ...recusados].map((it) => (
                <div key={it.id} className="vr-muted vr-small wl-inl-other">
                    {nomeItem(it)} — {ACAO_LABEL[it.acao] || it.acao}{it.motivo ? `: ${it.motivo}` : ''}
                </div>
            ))}
        </div>
    );
}
